import React, { useEffect, useId, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { makeDeposits, makeWithdrawals } from '@/api/custom';
import { fundingBankInitials } from '@/util/bank-link';
import type { FundingChip } from '@/util/bank-link';
import type { TransferMode } from './transfer-button-rules';

export type TransferCashTarget = {
  mode: TransferMode;
  accountId: string;
  accountName: string;
  accountTypeLabel: string;
  fundingChip?: FundingChip;
  bankLabel: string;
  bankDetail: string;
  bankLogo?: string | null;
  availableToWithdraw: number | null;
};

type TransferCashModalProps = {
  target: TransferCashTarget | null;
  onClose: () => void;
  onCompleted: (accountId: string) => void;
};

// The shared formatCurrency drops cents; a transfer must show the exact amount sent.
const formatTransferAmount = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

// Digits and one decimal point, at most two decimal places.
const sanitizeAmountInput = (raw: string): string => {
  const [whole, ...rest] = raw.replace(/[^\d.]/g, '').split('.');
  return rest.length > 0 ? `${whole}.${rest.join('').slice(0, 2)}` : whole;
};

// Whole cents, never above what's available. Rounding first avoids float
// error (0.29 * 100 is 28.999…), which a plain floor would turn into $0.28.
const maxWithdrawAmount = (available: number): string => {
  const cents = Math.round(available * 100);
  const capped = cents / 100 > available ? cents - 1 : cents;
  return (Math.max(capped, 0) / 100).toFixed(2);
};

const amountError = (mode: TransferMode, raw: string, available: number | null): string | null => {
  const value = Number(raw);
  if (!raw.trim() || !Number.isFinite(value) || value <= 0) {
    return 'Enter an amount greater than $0.';
  }
  if (mode === 'withdraw' && available != null && value > available) {
    return `You can withdraw up to ${formatTransferAmount(available)}.`;
  }
  return null;
};

const logoSrc = (logo?: string | null): string | undefined => {
  const value = logo?.trim();
  if (!value) {
    return undefined;
  }
  if (value.startsWith('data:') || value.startsWith('http') || value.startsWith('/')) {
    return value;
  }
  return `data:image/png;base64,${value}`;
};

const TransferCashModal: React.FC<TransferCashModalProps> = ({ target, onClose, onCompleted }) => {
  const titleId = useId();
  const openerRef = useRef<HTMLElement | null>(null);
  const [step, setStep] = useState<'amount' | 'review' | 'success'>('amount');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  // Bumped whenever the modal closes or opens for a new target, so a request
  // still in flight from an earlier transfer can't write into this one.
  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    if (!target) {
      return;
    }
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setStep('amount');
    setAmount('');
    setError(null);
    setSubmitting(false);
    submittingRef.current = false;
  }, [target]);

  const close = () => {
    requestIdRef.current += 1;
    // Done, X, Escape and a click outside all refresh after a successful transfer.
    if (step === 'success' && target) {
      onCompleted(target.accountId);
    }
    onClose();
    openerRef.current?.focus();
  };

  if (!target) {
    return null;
  }

  const numericAmount = Number(amount);
  const title =
    step === 'review'
      ? target.mode === 'deposit'
        ? 'Review deposit'
        : 'Review withdrawal'
      : target.mode === 'deposit'
        ? `Deposit to ${target.accountName}`
        : `Withdraw from ${target.accountName}`;
  const fromName = target.mode === 'deposit' ? target.bankLabel : target.accountName;
  const toName = target.mode === 'deposit' ? target.accountName : target.bankLabel;
  const fromDetail = target.mode === 'deposit' ? target.bankDetail : target.accountTypeLabel;
  const toDetail = target.mode === 'deposit' ? target.accountTypeLabel : target.bankDetail;
  const bankIsFrom = target.mode === 'deposit';

  const review = () => {
    const nextError = amountError(target.mode, amount, target.availableToWithdraw);
    setError(nextError);
    if (!nextError) {
      setStep('review');
    }
  };

  const confirm = async () => {
    const failureMessage =
      target.mode === 'deposit'
        ? "Couldn't submit your deposit. Try again."
        : "Couldn't submit your withdrawal. Try again.";
    if (submittingRef.current || !target.fundingChip?.plaidId) {
      setError(failureMessage);
      return;
    }
    const requestId = requestIdRef.current;
    const { accountId } = target;
    const isCurrent = () => requestIdRef.current === requestId;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const request = {
        amount: numericAmount,
        account_id: accountId,
        plaid_id: target.fundingChip.plaidId,
      };
      const response =
        target.mode === 'deposit'
          ? await makeDeposits([request])
          : await makeWithdrawals([request]);
      // The API answers 200 even when Apex rejects the transfer; rejected
      // requests come back in `failed`.
      if ((response?.failed?.length ?? 0) > 0) {
        console.warn('Transfer rejected', response.failed);
        if (isCurrent()) {
          setError(failureMessage);
        }
        return;
      }
      if (isCurrent()) {
        setStep('success');
      } else {
        // Closed before it finished: still refresh balances and transfers.
        onCompleted(accountId);
      }
    } catch (submitError) {
      console.error('Failed to submit transfer', submitError);
      if (isCurrent()) {
        setError(failureMessage);
      }
    } finally {
      if (isCurrent()) {
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  };

  return (
    <Modal
      open
      footer={null}
      width={500}
      onCancel={close}
      destroyOnClose
      maskClosable
      keyboard
      className="transfer-cash-modal"
      title={
        <h2 id={titleId} className="transfer-cash-title">
          {step === 'success' ? (target.mode === 'deposit' ? 'Deposit' : 'Withdrawal') : title}
        </h2>
      }
      styles={{ mask: { background: 'rgba(0, 0, 0, 0.55)' } }}
    >
      <div role="document">
        {step === 'amount' && (
          <div className="transfer-cash-step">
            <label className="transfer-cash-amount" htmlFor="transfer-cash-amount">
              <span className="transfer-cash-field-label">Amount</span>
              <span className={`transfer-cash-amount-box${error ? ' is-invalid' : ''}`}>
                <span className="transfer-cash-dollar">$</span>
                <input
                  id="transfer-cash-amount"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  aria-label="Amount"
                  onChange={(event) => {
                    setAmount(sanitizeAmountInput(event.target.value));
                    setError(null);
                  }}
                />
              </span>
            </label>
            {target.mode === 'withdraw' && target.availableToWithdraw != null && (
              <div className="transfer-cash-available">
                <span>Available to withdraw: {formatTransferAmount(target.availableToWithdraw)}</span>
                <button
                  type="button"
                  onClick={() => setAmount(maxWithdrawAmount(target.availableToWithdraw!))}
                >
                  Max
                </button>
              </div>
            )}
            <LockedParty
              label="From"
              name={fromName}
              detail={fromDetail}
              mark={bankIsFrom ? 'bank' : 'wavvest'}
              bankName={target.bankLabel}
              bankLogo={target.bankLogo}
            />
            <LockedParty
              label="To"
              name={toName}
              detail={toDetail}
              mark={bankIsFrom ? 'wavvest' : 'bank'}
              bankName={target.bankLabel}
              bankLogo={target.bankLogo}
            />
            {error && (
              <p className="transfer-cash-error" role="alert">
                {error}
              </p>
            )}
            <div className="transfer-cash-actions">
              <Button onClick={close}>Cancel</Button>
              <Button type="primary" onClick={review}>
                {target.mode === 'deposit' ? 'Review deposit' : 'Review withdrawal'}
              </Button>
            </div>
          </div>
        )}
        {step === 'review' && (
          <div className="transfer-cash-step">
            <div className="transfer-cash-review-hero">
              <span>{target.mode === 'deposit' ? "You're depositing" : "You're withdrawing"}</span>
              <strong>{formatTransferAmount(numericAmount)}</strong>
            </div>
            <dl className="transfer-cash-summary">
              <div>
                <dt>From</dt>
                <dd>{fromName}</dd>
              </div>
              <div>
                <dt>To</dt>
                <dd>{toName}</dd>
              </div>
              <div>
                {/* TODO: no estimated-arrival field on the deposit/withdraw API. */}
                <dt>Estimated arrival</dt>
                <dd>—</dd>
              </div>
            </dl>
            {error && (
              <p className="transfer-cash-error" role="alert">
                {error}
              </p>
            )}
            <div className="transfer-cash-actions is-split">
              <Button onClick={() => setStep('amount')} disabled={submitting}>
                Back
              </Button>
              <Button type="primary" loading={submitting} onClick={() => void confirm()}>
                {target.mode === 'deposit' ? 'Confirm deposit' : 'Confirm withdrawal'}
              </Button>
            </div>
          </div>
        )}
        {step === 'success' && (
          <div className="transfer-cash-success">
            <span className="transfer-cash-success-mark" aria-hidden>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12l5 5 9-10" />
              </svg>
            </span>
            <h2>
              {target.mode === 'deposit' ? 'Deposit submitted' : 'Withdrawal submitted'}
            </h2>
            <p>
              {formatTransferAmount(numericAmount)} from {fromName} to {toName}. You'll see it in Recent
              transfers.
            </p>
            <Button
              type="primary"
              onClick={close}
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

const PartyMark = ({
  mark,
  bankName,
  bankLogo,
}: {
  mark: 'bank' | 'wavvest';
  bankName: string;
  bankLogo?: string | null;
}) => {
  if (mark === 'wavvest') {
    return (
      <span className="transfer-cash-party-mark is-wavvest" aria-hidden>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M3 10c3-3 5-3 9 0s6 3 9 0" />
          <path d="M3 15c3-3 5-3 9 0s6 3 9 0" />
        </svg>
      </span>
    );
  }
  const image = logoSrc(bankLogo);
  if (image) {
    return <img className="transfer-cash-party-mark" src={image} alt="" />;
  }
  return (
    <span className="transfer-cash-party-mark is-bank" aria-hidden>
      {fundingBankInitials(bankName)}
    </span>
  );
};

const LockedParty = ({
  label,
  name,
  detail,
  mark,
  bankName,
  bankLogo,
}: {
  label: string;
  name: string;
  detail: string;
  mark: 'bank' | 'wavvest';
  bankName: string;
  bankLogo?: string | null;
}) => (
  <div className="transfer-cash-party-block">
    <span className="transfer-cash-field-label">{label}</span>
    <div className="transfer-cash-party">
      <PartyMark mark={mark} bankName={bankName} bankLogo={bankLogo} />
      <div className="transfer-cash-party-copy">
        <strong>{name}</strong>
        <span>{detail}</span>
      </div>
      <LockOutlined aria-hidden />
    </div>
  </div>
);

export default TransferCashModal;
